//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { fetchLiquidityAssetsForChainConfig } from '@react/fetchers/chainFetchers'

import { LYTPoolServiceConfigurationAbi, ServiceConfigurationAbi, ToSAcceptanceRegistryAbi } from '@shared/Abis'
import { chainIdToProvider } from '@shared/DeploymentConfigs'
import { ChainConfiguration } from '@shared/DeploymentConfigTypes'
import { ChainId } from '@shared/Enums'
import { PerimeterChainAddressesInterface } from '@shared/jest.shared.helper'
import { LiquidityAsset } from '@shared/LiquidityAsset'
import { logOT } from '@shared/loggingShared'
import { ETH_ADDR_ZERO, EthAddr } from '@shared/SchemaTypes'
import {
  SerivceAddressListV3Struct,
  SerivceAddressListV5Struct,
} from '@typechain-types/contracts/ServiceConfigurationV5'
import { Contract, ethers, Network, Provider, ZeroAddress } from 'ethers'

export const getServiceConfigurationAddrFromENS = async (
  provider: Provider,
  location?: string
): Promise<EthAddr | undefined> => {
  if (location) {
    logOT.debug('getServiceConfigurationAddrFromENS location ', location)
    // const network = await provider.getNetwork()

    // if (!network.ensAddress && Number(network.chainId) == CHAIN_ID_SEPOLIA) {
    //   network.ensAddress = '0x00000000000C2E074eC69A0dFb2997BA6C7d2e1e'
    // }
    try {
      logOT.debug('getServiceConfigurationAddrFromENS provider ', location)
      const serviceAddr = (await provider.resolveName(location)) as EthAddr | undefined
      logOT.debug('getServiceConfigurationAddrFromENS serviceAddr ', serviceAddr)
      return serviceAddr
    } catch (e) {
      logOT.debug('getServiceConfigurationAddrFromENS SKIPPINGerror ', e)
      return undefined
    }
  }
  return undefined
}

export const getChainPerimeterAddressesForChainConfiguration = async (
  chainConfiguration: ChainConfiguration,
  serverApiBaseUrl: string,
  restAPIKey: string,
  isLyt: boolean
): Promise<PerimeterChainAddressesInterface> => {
  const serviceAddr = chainConfiguration.serviceConfigurationAddr
  logOT.trace(
    'getChainPerimeterAddressesForChainConfiguration1',
    serviceAddr,
    chainConfiguration,
    chainConfiguration.frontendProviderUrl
  )
  const provider =
    chainConfiguration.chainConfigurationName === 'Hardhat' ||
    chainConfiguration.chainConfigurationName === 'LytHardhat'
      ? chainIdToProvider(ChainId.HARDHAT)
      : new ethers.JsonRpcProvider(
          chainConfiguration.frontendProviderUrl,
          new Network(chainConfiguration.chainName, chainConfiguration.chainId),
          {
            // staticNetwork: true,
            pollingInterval: 100,
            polling: false,
            batchMaxCount: 1,
          }
        )

  logOT.trace(
    'getChainPerimeterAddressesForChainConfiguration2',
    serviceAddr,
    chainConfiguration.frontendProviderUrl,
    'PROVIDER',
    provider
  )

  const liquidityAssets: LiquidityAsset[] = await fetchLiquidityAssetsForChainConfig(
    chainConfiguration,
    serverApiBaseUrl,
    restAPIKey
  )
  if (isLyt) {
    const serviceConfiguration = new Contract(serviceAddr, LYTPoolServiceConfigurationAbi, provider)
    const poolRegistryAddr = await serviceConfiguration.getPoolRegistry()
    const poolFactoryAddr = await serviceConfiguration.lytPoolFactory()
    const poolControllerFactoryAddr = ZeroAddress

    const perimeterAddresses: PerimeterChainAddressesInterface = {
      chainConfigName: chainConfiguration.chainConfigurationName,

      deploymentChainId: chainConfiguration.chainId,
      liquidityAssets,
      ToSAcceptanceRegistry: ZeroAddress,
      ServiceConfiguration: serviceAddr,
      PoolFactory: ZeroAddress,
      PoolFactoryFlex: ZeroAddress,
      PoolLibFlex: ZeroAddress,
      LoanFactory: ZeroAddress,
      QueryLib: ZeroAddress,
      PoolAdminWalletAddr: ZeroAddress,
      TosAcceptanceRegistryUrl: '',
      PoolLib: ZeroAddress,
      LoanLib: ZeroAddress,
      WithdrawControllerFactory: ZeroAddress,
      PoolRegistry: poolRegistryAddr,

      WithdrawDepositControllerFactoryFlex: ZeroAddress,
      VaultFactory: ZeroAddress,
      PoolAccessControlFactory: ZeroAddress,
      PoolControllerFactory: ZeroAddress,

      PoolControllerFactoryFlex: ZeroAddress,

      PoolFactoryDynamic: ZeroAddress,
      PoolControllerFactoryDynamic: ZeroAddress,
      PoolFactoryLyt: poolFactoryAddr,
      PoolControllerFactoryLyt: poolControllerFactoryAddr,
    }

    logOT.debug('Lyt perimeterAddresses ', perimeterAddresses)

    return perimeterAddresses
  }

  const serviceContract = new Contract(serviceAddr, ServiceConfigurationAbi, provider)

  let poolAdminWalletAddr: EthAddr
  let addrs: SerivceAddressListV5Struct
  try {
    logOT.info('serviceAddr getServiceAddressListV5', serviceAddr)
    poolAdminWalletAddr = await serviceContract.poolAdminWallet()

    logOT.info('serviceAddr poolAdminWalletAddr', poolAdminWalletAddr)
    addrs = await serviceContract.getServiceAddressListV5()
    logOT.trace('getChainPerimeterAddressesForChainConfiguration getServiceAddressListV5', addrs)
  } catch (_e) {
    logOT.trace('getChainPerimeterAddressesForChainConfiguration V3 access')

    poolAdminWalletAddr = await serviceContract.poolAdminWallet()
    logOT.trace('getChainPerimeterAddressesForChainConfiguration V3', poolAdminWalletAddr)

    // logOT.error('getChainPerimeterAddressesForChainConfiguration', e)
    const addrsV3: SerivceAddressListV3Struct = await serviceContract.getServiceAddressListV3()
    addrs = {
      liquidityAssets: addrsV3.liquidityAssets,
      poolFactoryAddress: addrsV3.poolFactoryAddress,
      poolFactoryFlexAddress: addrsV3.poolFactoryFlexAddress,
      poolLibFlexAddress: addrsV3.poolLibFlexAddress,
      loanFactory: addrsV3.loanFactory,
      queryLibAddress: addrsV3.queryLibAddress,
      tosAcceptanceRegistry: addrsV3.tosAcceptanceRegistry,
      poolLibAddress: addrsV3.poolLibAddress,
      loanLibAddress: addrsV3.loanLibAddress,
      withdrawControllerFactoryAddress: addrsV3.withdrawControllerFactoryAddress,
      poolRegistryAddress: addrsV3.poolRegistryAddress,
      withdrawDepositControllerFactoryFlexAddress: addrsV3.withdrawDepositControllerFactoryFlexAddress,
      vaultFactoryAddress: addrsV3.vaultFactoryAddress,
      poolAccessControlFactoryAddress: addrsV3.poolAccessControlFactoryAddress,
      poolControllerFactoryAddress: addrsV3.poolControllerFactoryAddress,
      poolControllerFactoryFlexAddress: addrsV3.poolControllerFactoryFlexAddress,
      poolFactoryDynamicAddress: ETH_ADDR_ZERO,
      poolControllerFactoryDynamicAddress: ETH_ADDR_ZERO,
    }
  }

  logOT.trace(
    'getChainPerimeterAddressesForChainConfiguration5',
    addrs.tosAcceptanceRegistry,
    addrs.liquidityAssets,
    addrs
  )

  let tosAcceptanceRegistryUrl = ''
  try {
    const tosAcceptanceRegistry = new Contract(
      addrs.tosAcceptanceRegistry as EthAddr,
      ToSAcceptanceRegistryAbi,
      provider
    )

    tosAcceptanceRegistryUrl = await tosAcceptanceRegistry.termsOfService()

    logOT.debug('tosAcceptanceRegistryUrl', tosAcceptanceRegistryUrl)
  } catch (e) {
    logOT.warn('tosAcceptanceRegistryUrl', e)
  }

  const perimeterAddresses: PerimeterChainAddressesInterface = {
    chainConfigName: chainConfiguration.chainConfigurationName,

    deploymentChainId: chainConfiguration.chainId,
    liquidityAssets,
    ToSAcceptanceRegistry: addrs.tosAcceptanceRegistry as EthAddr,
    ServiceConfiguration: serviceAddr,
    PoolFactory: addrs.poolFactoryAddress as EthAddr,
    PoolFactoryFlex: addrs.poolFactoryFlexAddress as EthAddr,
    PoolLibFlex: addrs.poolLibFlexAddress as EthAddr,
    LoanFactory: addrs.loanFactory as EthAddr,
    QueryLib: addrs.queryLibAddress as EthAddr,
    PoolAdminWalletAddr: poolAdminWalletAddr,
    TosAcceptanceRegistryUrl: tosAcceptanceRegistryUrl,
    PoolLib: addrs.poolLibAddress as EthAddr,
    LoanLib: addrs.loanLibAddress as EthAddr,
    WithdrawControllerFactory: addrs.withdrawControllerFactoryAddress as EthAddr,
    PoolRegistry: addrs.poolRegistryAddress as EthAddr,

    WithdrawDepositControllerFactoryFlex: addrs.withdrawDepositControllerFactoryFlexAddress as EthAddr,
    VaultFactory: addrs.vaultFactoryAddress as EthAddr,
    PoolAccessControlFactory: addrs.poolAccessControlFactoryAddress as EthAddr,
    PoolControllerFactory: addrs.poolControllerFactoryAddress as EthAddr,

    PoolControllerFactoryFlex: addrs.poolControllerFactoryFlexAddress as EthAddr,

    PoolFactoryDynamic: addrs.poolFactoryDynamicAddress as EthAddr,
    PoolControllerFactoryDynamic: addrs.poolControllerFactoryDynamicAddress as EthAddr,

    PoolFactoryLyt: ZeroAddress,
    PoolControllerFactoryLyt: ZeroAddress,
  }

  logOT.debug('perimeterAddresses ', perimeterAddresses)

  return perimeterAddresses
}

export async function isOperator(chainConfiguration: ChainConfiguration, accountAddr: EthAddr): Promise<boolean> {
  const providerReadOnly = new ethers.JsonRpcProvider(chainConfiguration.frontendProviderUrl)
  const serviceConfiguration = new Contract(
    chainConfiguration.serviceConfigurationAddr,
    ServiceConfigurationAbi,
    providerReadOnly
  )
  const operatorRole = await serviceConfiguration.OPERATOR_ROLE()
  const isOperator = await serviceConfiguration.hasRole(operatorRole, accountAddr)
  logOT.debug('isOperator ', isOperator)
  return isOperator
}

export async function isPoolAdminVerified(
  chainConfiguration: ChainConfiguration,
  accountAddr: EthAddr
): Promise<boolean> {
  const providerReadOnly = new ethers.JsonRpcProvider(chainConfiguration.frontendProviderUrl)
  const serviceConfiguration = new Contract(
    chainConfiguration.serviceConfigurationAddr,
    ServiceConfigurationAbi,
    providerReadOnly
  )
  logOT.debug('isPoolAdminVerified', chainConfiguration.serviceConfigurationAddr, accountAddr, chainConfiguration)
  const poolAdminRole = await serviceConfiguration.POOL_ADMIN_ROLE()

  const isOperator = await serviceConfiguration.hasRole(poolAdminRole, accountAddr)
  logOT.debug('isOperator ', isOperator)
  return isOperator
}
